// Universal idle screen-lock overlay, shared by every business-internal SPA
// (apps/web, apps/admin, apps/fulfillment). After `idle_lock_seconds` of no
// user input the overlay covers the viewport and requires a PIN to dismiss.
// The Supabase session is NEVER touched — this is a privacy screen, not a
// logout. The PIN + timeout live on `core.users` and are read/verified through
// the `core.rpc_*_lock_*` RPCs (see migration
// 20260703040000_core_user_screen_lock_pins.sql).
//
// Modes:
//   - enter   : the signed-in user types their own PIN (web/admin/fulfillment).
//   - create  : first-time enrollment; nobody is ever stranded behind a PIN
//               they never set.
//   - confirm : second leg of create (re-type to confirm).
//   - lockedout: cooldown after 5 wrong attempts.
//   - switch  : (fulfillment only) pick a teammate who has a PIN and verify
//               THEIR PIN to become the active packing operator — the tablet
//               stays logged in as one account.
//
// Cross-tab: a lock/unlock in one tab fans out to every tab of the same app
// via `localStorage` (`pc.<appKey>.idleLock`) + `BroadcastChannel`, mirroring
// the storage-event pattern the apps already use for active-context sync.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMyLockSettings,
  listStationOperators,
  setMyLockPin,
  verifyMyLockPin,
  verifyStationOperatorPin,
  type MyLockSettings,
  type StationOperator,
} from '@pc/db';
import { Button } from '../Button';

export interface ActiveOperator {
  userId: string;
  displayName: string;
}

export interface IdleLockProps {
  /** Per-app namespace for the cross-tab lock channel (e.g. 'web'). */
  appKey: string;
  /** Fulfillment shared-tablet mode: offer the teammate picker on unlock. */
  enableUserSwap?: boolean;
  /** Returns the active org id (fulfillment needs it to list teammates). */
  getActiveOrgId?: () => string | null;
  /** Called when the active packing operator changes (fulfillment). */
  onActiveOperatorChange?: (op: ActiveOperator | null) => void;
  /** App-level sign-out (clears active context + redirects to central auth). */
  onSignOut?: () => void;
}

type Mode = 'enter' | 'create' | 'confirm' | 'switch' | 'lockedout';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'pointerdown',
  'touchstart',
  'scroll',
  'wheel',
] as const;

// Settings-changed broadcast so the Settings page can tell a mounted lock to
// re-read the (possibly new) timeout / has-pin without a full reload.
export const LOCK_SETTINGS_CHANGED_EVENT = 'pc:lock-settings-changed';

export function IdleLock({
  appKey,
  enableUserSwap = false,
  getActiveOrgId,
  onActiveOperatorChange,
  onSignOut,
}: IdleLockProps) {
  const lockStorageKey = `pc.${appKey}.idleLock`;
  // Shared, cross-tab "last activity" timestamp. The idle countdown is
  // computed against the MOST RECENT activity across every tab of this app,
  // not just this tab's own events. Without this, a second (background) tab
  // gets no mouse/keyboard events, counts down independently, fires its idle
  // timer, and broadcasts `lock` to every tab — locking the one the operator
  // is actively using. (Operator report 2026-06-09: "the pin page pops up
  // when I am in the middle of using the site.")
  const activityStorageKey = `pc.${appKey}.lastActivity`;
  const [settings, setSettings] = useState<MyLockSettings | null>(null);
  const [locked, setLocked] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());
  const lockedRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Keep a ref in sync so the activity listeners (registered once) can read
  // the latest locked state without re-binding on every state change.
  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  // Record activity locally AND share it with sibling tabs so none of them
  // count this tab's user as idle.
  const markActivity = useCallback(
    (now: number) => {
      lastActivityRef.current = now;
      try {
        window.localStorage.setItem(activityStorageKey, String(now));
      } catch {
        // hardened browser; cross-tab activity sync degrades gracefully and
        // each tab falls back to its own lastActivityRef.
      }
    },
    [activityStorageKey],
  );

  // The effective "last activity" is the most recent across this tab and any
  // sibling tab (via the shared localStorage timestamp). The idle timer and
  // the visibility re-check both measure against this so activity anywhere
  // keeps every tab alive.
  const readLastActivity = useCallback((): number => {
    let shared = 0;
    try {
      shared = Number(window.localStorage.getItem(activityStorageKey)) || 0;
    } catch {
      // ignore; fall back to the local ref only
    }
    return Math.max(lastActivityRef.current, shared);
  }, [activityStorageKey]);

  // SECURITY: this MUST fail closed. We never synthesize `has_pin: false` on
  // error or while the fetch is in flight — doing so let the overlay render
  // the "create a PIN" enrollment pad and silently overwrite a real PIN
  // (operator report 2026-06-08). When settings are unknown the overlay
  // defaults to the "enter your PIN" pad; we just keep retrying so the real
  // `has_pin` lands as soon as the session/network recovers.
  const loadSettings = useCallback(() => {
    let attempt = 0;
    const tryLoad = () => {
      getMyLockSettings()
        .then((s) => {
          if (s) setSettings(s);
          else if (attempt++ < 5) window.setTimeout(tryLoad, 1500);
        })
        .catch(() => {
          if (attempt++ < 5) window.setTimeout(tryLoad, 1500);
        });
    };
    tryLoad();
  }, []);

  useEffect(() => {
    loadSettings();
    function onSettingsChanged() {
      loadSettings();
    }
    window.addEventListener(LOCK_SETTINGS_CHANGED_EVENT, onSettingsChanged);
    return () =>
      window.removeEventListener(LOCK_SETTINGS_CHANGED_EVENT, onSettingsChanged);
  }, [loadSettings]);

  const broadcast = useCallback(
    (type: 'lock' | 'unlock') => {
      try {
        window.localStorage.setItem(
          lockStorageKey,
          JSON.stringify({ type, at: Date.now() }),
        );
      } catch {
        // hardened browser; cross-tab sync degrades gracefully
      }
      channelRef.current?.postMessage(type);
    },
    [lockStorageKey],
  );

  const engageLock = useCallback(
    (fromRemote = false) => {
      if (lockedRef.current) return;
      lockedRef.current = true;
      setLocked(true);
      if (!fromRemote) broadcast('lock');
    },
    [broadcast],
  );

  const releaseLock = useCallback(
    (fromRemote = false) => {
      lockedRef.current = false;
      // Unlocking is activity: reset the shared countdown for every tab.
      markActivity(Date.now());
      setLocked(false);
      if (!fromRemote) broadcast('unlock');
    },
    [broadcast, markActivity],
  );

  // Cross-tab sync (BroadcastChannel + storage event fallback).
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(`pc-idle-lock-${appKey}`);
      channel.onmessage = (ev) => {
        if (ev.data === 'lock') engageLock(true);
        else if (ev.data === 'unlock') releaseLock(true);
      };
      channelRef.current = channel;
    }
    function onStorage(e: StorageEvent) {
      if (e.key !== lockStorageKey || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue) as { type?: string };
        if (parsed.type === 'lock') engageLock(true);
        else if (parsed.type === 'unlock') releaseLock(true);
      } catch {
        // ignore malformed payloads
      }
    }
    window.addEventListener('storage', onStorage);
    // Adopt a lock that another tab set before this tab mounted.
    try {
      const raw = window.localStorage.getItem(lockStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { type?: string };
        if (parsed.type === 'lock') engageLock(true);
      }
    } catch {
      // ignore
    }
    return () => {
      window.removeEventListener('storage', onStorage);
      channel?.close();
      channelRef.current = null;
    };
  }, [appKey, lockStorageKey, engageLock, releaseLock]);

  // Activity tracking. Throttled so a moving mouse doesn't thrash storage.
  useEffect(() => {
    let throttle = 0;
    function onActivity() {
      if (lockedRef.current) return;
      const now = Date.now();
      if (now - throttle < 1000) return;
      throttle = now;
      markActivity(now);
    }
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    function onVisibility() {
      // A long background stint should count toward idle, so don't reset
      // on return — just re-check immediately (against shared activity).
      if (!lockedRef.current && document.visibilityState === 'visible') {
        const idleMs = (settings?.idle_lock_seconds ?? 60) * 1000;
        if (Date.now() - readLastActivity() >= idleMs) engageLock();
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [settings?.idle_lock_seconds, engageLock, markActivity, readLastActivity]);

  // The idle timer. Measures against the most recent activity across ALL
  // tabs, so a background tab no longer locks the tab the operator is using.
  useEffect(() => {
    const idleMs = (settings?.idle_lock_seconds ?? 60) * 1000;
    const timer = window.setInterval(() => {
      if (lockedRef.current) return;
      if (Date.now() - readLastActivity() >= idleMs) {
        engageLock();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [settings?.idle_lock_seconds, engageLock, readLastActivity]);

  if (!locked) return null;

  return (
    <LockOverlay
      settings={settings}
      enableUserSwap={enableUserSwap}
      getActiveOrgId={getActiveOrgId}
      onActiveOperatorChange={onActiveOperatorChange}
      onSignOut={onSignOut}
      onUnlocked={() => releaseLock()}
      onSettingsRefetch={loadSettings}
    />
  );
}

function LockOverlay({
  settings,
  enableUserSwap,
  getActiveOrgId,
  onActiveOperatorChange,
  onSignOut,
  onUnlocked,
  onSettingsRefetch,
}: {
  settings: MyLockSettings | null;
  enableUserSwap?: boolean;
  getActiveOrgId?: () => string | null;
  onActiveOperatorChange?: (op: ActiveOperator | null) => void;
  onSignOut?: () => void;
  onUnlocked: () => void;
  onSettingsRefetch: () => void;
}) {
  const { t } = useTranslation();
  // `settings === null` means "not loaded yet" — we must NOT treat that as
  // "no PIN". The enrollment (create) pad is only ever reachable once the
  // server has *definitively* told us this user has no PIN.
  const hasPin = settings?.has_pin === true;
  const hasPinKnown = settings !== null;

  // SECURITY: default to the "enter your PIN" pad, never "create". When the
  // settings fetch is still in flight (e.g. right after a reload adopts a
  // lock from localStorage) the user types their real PIN and it verifies
  // server-side. We only flip to the enrollment pad below, once we have a
  // definitive has_pin === false.
  const [mode, setMode] = useState<Mode>(() =>
    enableUserSwap ? 'switch' : settings?.has_pin === false ? 'create' : 'enter',
  );

  // Reconcile the pad once real settings land (the initial mode was chosen
  // before the async fetch resolved). This is what stops the "reload shows
  // Create a PIN even though I have one" bypass: the moment we learn has_pin
  // is true, we force the create/confirm enrollment pad back to "enter".
  useEffect(() => {
    if (enableUserSwap || !hasPinKnown) return;
    setMode((m) => {
      if (hasPin) return m === 'create' || m === 'confirm' ? 'enter' : m;
      return m === 'enter' ? 'create' : m;
    });
  }, [enableUserSwap, hasPinKnown, hasPin]);
  const [pin, setPin] = useState('');
  const [firstPin, setFirstPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operators, setOperators] = useState<StationOperator[] | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<StationOperator | null>(null);

  // Load the teammate roster for the fulfillment switch-user picker.
  useEffect(() => {
    if (mode !== 'switch') return;
    const orgId = getActiveOrgId?.() ?? null;
    if (!orgId) {
      setOperators([]);
      return;
    }
    let alive = true;
    listStationOperators(orgId)
      .then((rows) => {
        if (alive) setOperators(rows);
      })
      .catch(() => {
        if (alive) setOperators([]);
      });
    return () => {
      alive = false;
    };
  }, [mode, getActiveOrgId]);

  function reset() {
    setPin('');
    setFirstPin('');
    setError(null);
  }

  function pushDigit(d: string) {
    setError(null);
    setPin((prev) => (prev.length >= 8 ? prev : prev + d));
  }

  function backspace() {
    setError(null);
    setPin((prev) => prev.slice(0, -1));
  }

  function messageFor(err: unknown, fallback: string): string {
    if (err instanceof Error && err.message) return err.message;
    return fallback;
  }

  // PostgREST surfaces the RPC's SQLSTATE on the error object. 42710
  // (duplicate_object) is raised by rpc_set_my_lock_pin when a PIN already
  // exists. We also sniff the message as a belt-and-suspenders fallback.
  function isAlreadySetError(err: unknown): boolean {
    const code = (err as { code?: unknown } | null)?.code;
    if (code === '42710') return true;
    return err instanceof Error && /already set/i.test(err.message);
  }

  async function submitEnter() {
    if (pin.length < 4) {
      setError(t('screenLock.error.tooShort'));
      return;
    }
    setBusy(true);
    try {
      const ok = await verifyMyLockPin(pin);
      if (ok) {
        reset();
        onUnlocked();
      } else {
        setPin('');
        setError(t('screenLock.error.incorrect'));
      }
    } catch (err) {
      setPin('');
      setError(messageFor(err, t('screenLock.error.generic')));
    } finally {
      setBusy(false);
    }
  }

  async function submitCreate() {
    if (pin.length < 4) {
      setError(t('screenLock.error.tooShort'));
      return;
    }
    // First leg: stash and ask for confirmation.
    if (mode === 'create') {
      setFirstPin(pin);
      setPin('');
      setMode('confirm');
      setError(null);
      return;
    }
    // Confirm leg.
    if (pin !== firstPin) {
      setPin('');
      setFirstPin('');
      setMode('create');
      setError(t('screenLock.error.mismatch'));
      return;
    }
    setBusy(true);
    try {
      // Enrollment only. The server (rpc_set_my_lock_pin) refuses to clobber
      // an existing PIN — rotating must go through Settings, which requires
      // the current PIN. So this can ONLY succeed for a genuinely
      // PIN-less account; it can never overwrite-and-bypass.
      await setMyLockPin(firstPin);
      onSettingsRefetch();
      reset();
      onUnlocked();
    } catch (err) {
      onSettingsRefetch();
      reset();
      // 42710 = a PIN already exists (e.g. settings hadn't loaded when the
      // enrollment pad rendered). Don't strand the user on a dead create
      // pad — send them to the "enter your PIN" pad instead.
      if (isAlreadySetError(err)) {
        setMode('enter');
        setError(t('screenLock.error.alreadySet'));
      } else {
        setMode('create');
        setError(messageFor(err, t('screenLock.error.generic')));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitSwitch() {
    if (!selectedOperator) return;
    if (pin.length < 4) {
      setError(t('screenLock.error.tooShort'));
      return;
    }
    const orgId = getActiveOrgId?.() ?? null;
    if (!orgId) {
      setError(t('screenLock.error.generic'));
      return;
    }
    setBusy(true);
    try {
      const op = await verifyStationOperatorPin(orgId, selectedOperator.user_id, pin);
      onActiveOperatorChange?.({ userId: op.user_id, displayName: op.display_name });
      reset();
      setSelectedOperator(null);
      onUnlocked();
    } catch (err) {
      setPin('');
      setError(messageFor(err, t('screenLock.error.incorrect')));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit() {
    if (busy) return;
    if (mode === 'enter') void submitEnter();
    else if (mode === 'create' || mode === 'confirm') void submitCreate();
    else if (mode === 'switch') void submitSwitch();
  }

  // Switch-user roster step (no operator picked yet): show the teammate list.
  const showRoster = mode === 'switch' && !selectedOperator;

  const subtitle =
    mode === 'enter'
      ? t('screenLock.subtitle.enterPin')
      : mode === 'create'
        ? t('screenLock.subtitle.createPin')
        : mode === 'confirm'
          ? t('screenLock.subtitle.confirmPin')
          : showRoster
            ? t('screenLock.subtitle.switchUser')
            : t('screenLock.subtitle.enterPinFor', {
                name: selectedOperator?.display_name ?? '',
              });

  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-paper/95 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('screenLock.title.locked')}
    >
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <p className="smallcap text-muted">{t('screenLock.title.locked')}</p>
          <p className="mt-2 text-sm text-ink-2">{subtitle}</p>
        </div>

        {error ? (
          <div
            role="alert"
            className="mb-4 border border-rule border-l-4 border-l-clay bg-linen px-4 py-2 text-sm text-ink-2"
          >
            {error}
          </div>
        ) : null}

        {showRoster ? (
          <OperatorRoster
            operators={operators}
            onPick={(op) => {
              setSelectedOperator(op);
              reset();
            }}
          />
        ) : (
          <PinPad
            pin={pin}
            disabled={busy}
            onDigit={pushDigit}
            onBackspace={backspace}
            onSubmit={onSubmit}
            submitLabel={
              mode === 'create'
                ? t('screenLock.action.next')
                : mode === 'confirm'
                  ? t('screenLock.action.confirm')
                  : t('screenLock.action.unlock')
            }
          />
        )}

        <div className="mt-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {/* Fulfillment: jump between "switch user" and self-enroll. */}
            {enableUserSwap && !showRoster ? (
              <button
                type="button"
                className="text-xs text-muted underline hover:text-ink"
                disabled={busy}
                onClick={() => {
                  setSelectedOperator(null);
                  setMode('switch');
                  reset();
                }}
              >
                {t('screenLock.action.back')}
              </button>
            ) : null}
            {enableUserSwap && showRoster ? (
              <button
                type="button"
                className="text-xs text-muted underline hover:text-ink"
                disabled={busy}
                onClick={() => {
                  setMode('create');
                  reset();
                }}
              >
                {t('screenLock.action.setUpPin')}
              </button>
            ) : null}
          </div>
          {onSignOut ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onSignOut}>
              {t('screenLock.action.signOut')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function OperatorRoster({
  operators,
  onPick,
}: {
  operators: StationOperator[] | null;
  onPick: (op: StationOperator) => void;
}) {
  const { t } = useTranslation();
  if (operators === null) {
    return <p className="text-center text-sm text-muted">{t('screenLock.loading')}</p>;
  }
  if (operators.length === 0) {
    return (
      <p className="text-center text-sm text-muted">
        {t('screenLock.noOperators')}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2 max-h-72 overflow-y-auto">
      {operators.map((op) => (
        <li key={op.user_id}>
          <button
            type="button"
            onClick={() => onPick(op)}
            className="w-full border border-rule bg-paper px-4 py-3 text-left text-sm text-ink hover:bg-linen"
          >
            {op.display_name}
          </button>
        </li>
      ))}
    </ul>
  );
}

function PinPad({
  pin,
  disabled,
  onDigit,
  onBackspace,
  onSubmit,
  submitLabel,
}: {
  pin: string;
  disabled: boolean;
  onDigit: (d: string) => void;
  onBackspace: () => void;
  onSubmit: () => void;
  submitLabel: string;
}) {
  // Allow a hardware keyboard alongside the on-screen pad (warehouse tablets
  // use the pad; desktop users type).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (disabled) return;
      if (e.key >= '0' && e.key <= '9') {
        onDigit(e.key);
      } else if (e.key === 'Backspace') {
        onBackspace();
      } else if (e.key === 'Enter') {
        onSubmit();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, onDigit, onBackspace, onSubmit]);

  const dots: ReactNode[] = [];
  const shown = Math.max(pin.length, 4);
  for (let i = 0; i < shown; i += 1) {
    dots.push(
      <span
        key={i}
        className={`h-3 w-3 rounded-full border ${
          i < pin.length ? 'bg-ink border-ink' : 'border-rule-strong'
        }`}
      />,
    );
  }

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-center gap-2 h-8" aria-hidden>
        {dots}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            disabled={disabled}
            onClick={() => onDigit(k)}
            className="h-14 border border-rule bg-paper text-xl tabular text-ink hover:bg-linen disabled:opacity-50"
          >
            {k}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={onBackspace}
          className="h-14 border border-rule bg-paper text-sm text-muted hover:bg-linen disabled:opacity-50"
          aria-label="Backspace"
        >
          ⌫
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onDigit('0')}
          className="h-14 border border-rule bg-paper text-xl tabular text-ink hover:bg-linen disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          disabled={disabled || pin.length < 4}
          onClick={onSubmit}
          className="h-14 border border-ink bg-ink text-sm text-paper hover:bg-ink/90 disabled:opacity-40"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
